import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Heart, Star, Tag, ImageOff } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useCart } from "../context/CartContext";
import { useToast } from "../hooks/use-toast";

const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const { toast } = useToast();

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, 1);
    toast({
      title: "Ajouté au panier",
      description: product.name,
    });
  };

  return (
    <Link
      to={`/produit/${product.id}`}
      className="group bg-white border border-neutral-200 rounded-lg overflow-hidden hover:shadow-xl hover:border-[#0b2960]/30 transition-all duration-300 flex flex-col"
    >
      <div className="relative aspect-square bg-neutral-100 overflow-hidden">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-neutral-400 gap-2 bg-gradient-to-br from-neutral-100 to-neutral-200">
            <ImageOff className="h-10 w-10" />
            <span className="text-[10px] uppercase tracking-widest font-semibold">Photo en attente</span>
          </div>
        )}
        {product.discount > 0 && (
          <Badge className="absolute top-2 left-2 bg-[#e11d24] hover:bg-[#e11d24] text-white text-xs font-bold px-2 py-1 rounded">
            -{product.discount}%
          </Badge>
        )}
        {product.isFeatured && (
          <div
            className="absolute bottom-3 left-3 pointer-events-none flex items-center justify-center text-neutral-900 font-black text-lg leading-none drop-shadow-md"
            style={{
              width: 68,
              height: 68,
              background:
                "conic-gradient(from 0deg, #fff447 0deg, #ffe605 30deg, #fff447 60deg, #ffe605 90deg, #fff447 120deg, #ffe605 150deg, #fff447 180deg, #ffe605 210deg, #fff447 240deg, #ffe605 270deg, #fff447 300deg, #ffe605 330deg, #fff447 360deg)",
              clipPath:
                "polygon(50% 0%, 61% 20%, 82% 12%, 76% 34%, 100% 40%, 82% 55%, 96% 74%, 74% 74%, 76% 96%, 55% 82%, 40% 100%, 40% 78%, 18% 82%, 26% 62%, 4% 55%, 22% 40%, 4% 26%, 26% 26%, 20% 4%, 40% 22%)",
              transform: "rotate(-8deg)",
              border: "2px solid #e11d24",
            }}
          >
            <span className="text-center text-[15px]">
              {product.price}€
            </span>
          </div>
        )}
        {product.condition === "Occasion" && (
          <Badge className="absolute top-2 right-2 bg-[#0b2960] hover:bg-[#0b2960] text-white text-[10px] font-bold px-2 py-1 rounded flex items-center gap-1">
            <Tag className="h-3 w-3" /> OCCASION
          </Badge>
        )}
        {product.isNew && product.condition !== "Occasion" && (
          <Badge className="absolute top-2 right-2 bg-green-600 hover:bg-green-600 text-white text-[10px] font-bold px-2 py-1 rounded">
            NEUF
          </Badge>
        )}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          className="absolute bottom-2 right-2 h-9 w-9 rounded-full bg-white/90 hover:bg-white text-neutral-700 hover:text-[#e11d24] flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Ajouter aux favoris"
        >
          <Heart className="h-4 w-4" />
        </button>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-1">{product.brand}</div>
        <h3 className="text-sm font-semibold text-neutral-800 line-clamp-2 min-h-[2.5rem] group-hover:text-[#0b2960] transition-colors">
          {product.name}
        </h3>

        <div className="flex items-center gap-0.5 mt-2">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
          ))}
          <span className="text-[11px] text-neutral-500 ml-1">(0)</span>
        </div>

        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-lg font-extrabold text-[#e11d24]">{product.price.toFixed(2)} €</span>
          {product.oldPrice > product.price && (
            <span className="text-xs text-neutral-400 line-through">{product.oldPrice.toFixed(2)} €</span>
          )}
        </div>

        <Button
          onClick={handleAdd}
          className="mt-3 w-full bg-[#0b2960] hover:bg-[#081e47] text-white text-xs h-9 rounded-md"
        >
          <ShoppingCart className="h-4 w-4 mr-1.5" /> Ajouter au panier
        </Button>
      </div>
    </Link>
  );
};

export default ProductCard;
